/** Shared Product dropdown, footer groups, breadcrumbs, and next-step chain. */

import { AI_ASSIST_NAV_GROUP, AI_ASSIST_PAGE } from "./aiAssistCopy";

export { AI_ASSIST_NAV_GROUP };

/** Hub page — first item in the Product menu, also in the footer. */
export const PRODUCT_HUB_LINK = {
  to: "/bowling-tournament-software",
  label: "Bowling tournament software",
};

export const PRODUCT_NAV_GROUPS = [
  {
    id: "before",
    label: "Before",
    items: [
      {
        to: "/bowling-tournament-signups",
        label: "Signups",
      },
      { to: "/bowling-tournament-card-payments", label: "Card payments" },
      {
        to: "/bowling-tournament-averages",
        label: "Averages",
      },
    ],
  },
  {
    id: "tournament-day",
    label: "Tournament day",
    items: [
      {
        to: "/bowling-tournament-scoring",
        label: "Scoring",
      },
      { to: "/bowling-bracket-software", label: "Brackets" },
    ],
  },
  {
    id: "after",
    label: "After",
    items: [
      {
        to: "/bowling-tournament-payouts",
        label: "Payouts",
        children: [
          {
            to: "/bowling-tournament-check-exports",
            label: "Check exports",
          },
        ],
      },
      { to: "/bowling-payout-calculator", label: "Payout calculator" },
    ],
  },
];

/** Product menu and footer: AI Assist first, then the weekend phases. */
export const PRODUCT_MENU_GROUPS = [AI_ASSIST_NAV_GROUP, ...PRODUCT_NAV_GROUPS];

const footerItems = (group) =>
  group.items.flatMap((item) => [
    { to: item.to, label: item.label },
    ...(item.children || []).map((child) => ({
      to: child.to,
      label: child.label,
      nested: true,
    })),
  ]);

export const FOOTER_PRODUCT_LINKS = [
  PRODUCT_HUB_LINK,
  { to: "/pricing", label: "Plans" },
  ...PRODUCT_MENU_GROUPS.flatMap((group) => [
    ...(group.to ? [{ to: group.to, label: group.label }] : []),
    ...footerItems(group),
  ]),
];

/** Primary flow for Next step cards on product pages. */
export const PRODUCT_NEXT_STEP = {
  "/bowling-tournament-signups": {
    to: "/bowling-tournament-averages",
    label: "Next: Averages",
    detail: "Enter or verify averages before you post games.",
  },
  "/bowling-tournament-signup-sheet-import": {
    to: "/bowling-tournament-averages",
    label: "Next: Averages",
    detail: "Verify averages after you create the teams.",
  },
  "/bowling-tournament-card-payments": {
    to: "/bowling-tournament-averages",
    label: "Next: Averages",
    detail: "Verify averages while you take Pay now and desk payments.",
  },
  "/bowling-tournament-averages": {
    to: "/bowling-tournament-scoring",
    label: "Next: Scoring",
    detail: "Post games once the averages are set.",
  },
  "/bowling-tournament-average-import": {
    to: "/bowling-tournament-scoring",
    label: "Next: Scoring",
    detail: "Use the averages when you build teams and post games.",
  },
  "/bowling-tournament-scoring": {
    to: "/bowling-tournament-payouts",
    label: "Next: Payouts",
    detail: "Lock prize funds and side pots when the board is final.",
  },
  "/bowling-tournament-recap-sheet-import": {
    to: "/bowling-tournament-payouts",
    label: "Next: Payouts",
    detail: "Side pots and standings use the same scores you just posted.",
  },
  "/bowling-bracket-software": {
    to: "/bowling-tournament-payouts",
    label: "Next: Payouts",
    detail: "Pay brackets and prize funds from the same weekend.",
  },
  "/bowling-tournament-payouts": {
    to: "/bowling-tournament-check-exports",
    label: "Next: Check exports",
    detail: "Print checks from the locked payout amounts.",
  },
  "/bowling-tournament-check-exports": {
    to: "/bowling-tournament-signups",
    label: "Back to Signups",
    detail: "Start the next tournament with online signups.",
  },
  "/bowling-payout-calculator": {
    to: "/bowling-tournament-payouts",
    label: "Next: Payouts",
    detail: "Lock the same ratios on Finance (payouts) when the tournament is live.",
  },
};

export const PRODUCT_BREADCRUMBS = {
  "/bowling-tournament-software": [
    { label: "Home", to: "/" },
    { label: "Bowling tournament software" },
  ],
  "/pricing": [
    { label: "Home", to: "/" },
    { label: "Plans" },
  ],
  "/bowling-tournament-signups": [
    { label: "Home", to: "/" },
    { label: "Signups" },
  ],
  "/bowling-tournament-signup-sheet-import": [
    { label: "Home", to: "/" },
    { label: "Signups", to: "/bowling-tournament-signups" },
    { label: "Signup sheet import" },
  ],
  "/bowling-tournament-card-payments": [
    { label: "Home", to: "/" },
    { label: "Card payments" },
  ],
  "/bowling-tournament-averages": [
    { label: "Home", to: "/" },
    { label: "Averages" },
  ],
  "/bowling-tournament-average-import": [
    { label: "Home", to: "/" },
    { label: "Averages", to: "/bowling-tournament-averages" },
    { label: "Average import" },
  ],
  "/bowling-tournament-scoring": [
    { label: "Home", to: "/" },
    { label: "Scoring" },
  ],
  "/bowling-tournament-recap-sheet-import": [
    { label: "Home", to: "/" },
    { label: "Scoring", to: "/bowling-tournament-scoring" },
    { label: "Recap sheet import" },
  ],
  "/bowling-bracket-software": [
    { label: "Home", to: "/" },
    { label: "Brackets" },
  ],
  "/bowling-tournament-payouts": [
    { label: "Home", to: "/" },
    { label: "Payouts" },
  ],
  "/bowling-tournament-check-exports": [
    { label: "Home", to: "/" },
    { label: "Payouts", to: "/bowling-tournament-payouts" },
    { label: "Check exports" },
  ],
  "/bowling-payout-calculator": [
    { label: "Home", to: "/" },
    { label: "Payout calculator" },
  ],
  [AI_ASSIST_PAGE.to]: [
    { label: "Home", to: "/" },
    { label: AI_ASSIST_PAGE.label },
  ],
};

export const pathIsInProductNav = (pathname) => {
  if (
    pathname === "/pricing" ||
    pathname === PRODUCT_HUB_LINK.to ||
    pathname === AI_ASSIST_PAGE.to
  ) {
    return true;
  }
  return PRODUCT_MENU_GROUPS.some((group) =>
    group.items.some(
      (item) =>
        item.to === pathname ||
        (item.children || []).some((child) => child.to === pathname)
    )
  );
};
